import { DragEvent, KeyboardEvent, MouseEvent, ReactNode, useState } from 'react'
import { FaTimes } from 'react-icons/fa'
import { DecorativeSpreadsheet } from '../decorativeSpreadsheet/DecorativeSpreadsheet'
import { ProgressBar } from '../progressBar/ProgressBar'
import s from './styles.module.scss'

export interface BatchDetail {
    label: string
    value: string
}

/**
 * choose   — prompt + file picker
 * ready    — chosen file + start button
 * confirm  — title / bureau coverage pause before AffiliaTech spend
 * verifying — spinner (upload parse + duplicate check)
 * running  — progress bar
 */
export type BatchWidgetStep = 'choose' | 'ready' | 'confirm' | 'running' | 'verifying'

/** One stretch of a multi-phase run, shown as a badge above the bar. */
export interface BatchPhaseStep {
    label: string
    state: 'done' | 'active' | 'pending'
}

interface Props {
    step?: BatchWidgetStep
    /** choose step */
    title: string
    subtitle?: ReactNode
    chooseAction?: ReactNode
    /** Filename drawn on the decorative spreadsheet in the choose step. */
    sampleFileName?: string
    /** Makes the whole card a click target during the choose step. */
    onChoose?: () => void
    /** Dropped files from Finder / Explorer. */
    onFilesDropped?: (files: File[]) => void
    /** ready step */
    fileName?: string
    readyAction?: ReactNode
    /** Clears the chosen file and returns to the choose step. */
    onClearFile?: () => void
    /** confirm step — pause before spend when Title coverage is incomplete */
    confirmTitle?: string
    confirmBody?: ReactNode
    confirmAction?: ReactNode
    /** running step */
    runningTitle?: string
    progress?: number
    progressLabel?: string
    /** Badges above the bar, so a bar that restarts reads as the next phase. */
    phaseSteps?: BatchPhaseStep[]
    /**
     * Hide the bar for stretches that have no row progress (duplicate check).
     * Phase badges and the label still show.
     */
    showProgress?: boolean
    /** Slot under the bar. Laid out as a row, so two children sit at each end. */
    progressFooter?: ReactNode
    /** Rows shown in the shelf that slides out from under the card. */
    details?: BatchDetail[]
    /** Drives the shelf animation. */
    shelfOpen?: boolean
    /** Shown top-right of the card once the shelf is open (run finished). */
    cornerAction?: ReactNode
    /** Overrides the shelf-driven reveal, for a control that should always show. */
    cornerActionShown?: boolean
}

export const BatchWidgetCard = ({
    step = 'choose',
    title,
    subtitle,
    chooseAction,
    sampleFileName = 'clients.csv',
    onChoose,
    onFilesDropped,
    fileName,
    readyAction,
    onClearFile,
    confirmTitle = 'Some required fields are missing',
    confirmBody,
    confirmAction,
    runningTitle = 'Batch run',
    progress = 0,
    progressLabel,
    phaseSteps = [],
    showProgress = true,
    progressFooter,
    details = [],
    shelfOpen = false,
    cornerAction,
    cornerActionShown,
}: Props) => {
    const [dragActive, setDragActive] = useState(false)
    const cornerVisible = cornerActionShown ?? shelfOpen
    const panelClass = (panelStep: BatchWidgetStep) =>
        [
            s.panel,
            step === panelStep ? s.panelVisible : s.panelHidden,
            panelStep === 'confirm' ? s.panelConfirm : '',
        ]
            .filter(Boolean)
            .join(' ')

    const isChoosable = step === 'choose' && Boolean(onChoose)
    const canDrop = Boolean(onFilesDropped) && step !== 'running' && step !== 'verifying' && step !== 'confirm'

    // The inner Choose file button fires its own handler, so ignore clicks that
    // came from a control inside the card.
    const handleCardClick = (event: MouseEvent<HTMLDivElement>) => {
        if (!isChoosable) return
        if ((event.target as HTMLElement).closest('button, input, textarea, select, label, a')) return
        onChoose?.()
    }

    const handleCardKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!isChoosable) return
        if (event.key !== 'Enter' && event.key !== ' ') return
        if ((event.target as HTMLElement).closest('button, input, textarea, select, label, a')) return
        event.preventDefault()
        onChoose?.()
    }

    const handleDragEnter = (event: DragEvent<HTMLDivElement>) => {
        if (!canDrop) return
        event.preventDefault()
        event.stopPropagation()
        if (event.dataTransfer.types.includes('Files')) setDragActive(true)
    }

    const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
        if (!canDrop) return
        event.preventDefault()
        event.stopPropagation()
        event.dataTransfer.dropEffect = 'copy'
    }

    const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
        if (!canDrop) return
        event.preventDefault()
        event.stopPropagation()
        const next = event.relatedTarget as Node | null
        if (next && event.currentTarget.contains(next)) return
        setDragActive(false)
    }

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
        if (!canDrop) return
        event.preventDefault()
        event.stopPropagation()
        setDragActive(false)
        const files = Array.from(event.dataTransfer.files || [])
        if (files.length) onFilesDropped?.(files)
    }

    return (
        <div className={s.wrapper}>
            <div
                className={`${s.card} ${isChoosable ? s.cardChoosable : ''} ${
                    dragActive ? s.cardDropTarget : ''
                }`}
                onClick={handleCardClick}
                onKeyDown={handleCardKeyDown}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                role={isChoosable ? 'button' : undefined}
                tabIndex={isChoosable ? 0 : undefined}
            >
                {cornerAction && (
                    <div
                        className={`${s.cornerAction} ${cornerVisible ? s.cornerActionVisible : ''}`}
                        aria-hidden={!cornerVisible}
                    >
                        {cornerAction}
                    </div>
                )}

                {/* Choose/ready/etc. share height so uploading a file does not shrink the card. */}
                <div className={s.stage}>
                    <div className={panelClass('choose')} aria-hidden={step !== 'choose'}>
                        <div className={s.decoration}>
                            <DecorativeSpreadsheet variant="skeleton" />
                        </div>
                        <div className={s.title}>{title}</div>
                        {subtitle && <div className={s.subtitle}>{subtitle}</div>}
                        {chooseAction && <div className={s.action}>{chooseAction}</div>}
                    </div>

                    <div className={panelClass('ready')} aria-hidden={step !== 'ready'}>
                        <div className={s.decoration}>
                            <DecorativeSpreadsheet variant="filled" label={fileName ?? sampleFileName} />
                        </div>
                        <div className={s.fileRow}>
                            {fileName && <span className={s.fileName}>{fileName}</span>}
                            {onClearFile && (
                                <button
                                    type="button"
                                    className={s.clearButton}
                                    onClick={onClearFile}
                                    aria-label="Remove file"
                                    title="Remove file"
                                >
                                    <FaTimes aria-hidden="true" />
                                </button>
                            )}
                        </div>
                        {readyAction && <div className={s.action}>{readyAction}</div>}
                    </div>

                    <div className={panelClass('confirm')} aria-hidden={step !== 'confirm'}>
                        <div className={s.title}>{confirmTitle}</div>
                        {fileName && <div className={s.subtitle}>{fileName}</div>}
                        {confirmBody && <div className={s.confirmBody}>{confirmBody}</div>}
                        {confirmAction && <div className={s.action}>{confirmAction}</div>}
                    </div>

                    <div className={panelClass('verifying')} aria-hidden={step !== 'verifying'}>
                        <div className={s.verifying}>
                            <span className={s.verifyingSpinner} aria-hidden="true" />
                            <div className={s.title}>{runningTitle || 'Verifying file...'}</div>
                            {fileName && <div className={s.subtitle}>{fileName}</div>}
                            {progressLabel && <div className={s.verifyingLabel}>{progressLabel}</div>}
                        </div>
                    </div>

                    <div className={panelClass('running')} aria-hidden={step !== 'running'}>
                        <div className={s.runningTitleRow}>
                            <span className={s.runningSpinner} aria-hidden="true" />
                            <span className={s.title}>{runningTitle}</span>
                        </div>
                        {fileName && <div className={s.subtitle}>{fileName}</div>}
                        {phaseSteps.length > 0 && (
                            <ol className={s.phaseSteps}>
                                {phaseSteps.map((phaseStep, index) => (
                                    <li
                                        key={phaseStep.label}
                                        className={`${s.phaseStep} ${s[`phaseStep_${phaseStep.state}`]}`}
                                        aria-current={phaseStep.state === 'active' ? 'step' : undefined}
                                    >
                                        <span className={s.phaseStepIndex} aria-hidden="true">
                                            {phaseStep.state === 'done' ? '✓' : index + 1}
                                        </span>
                                        {phaseStep.label}
                                    </li>
                                ))}
                            </ol>
                        )}
                        {showProgress ? (
                            <div className={s.progress}>
                                <ProgressBar value={progress} label={progressLabel} />
                                {progressFooter && <div className={s.progressFooter}>{progressFooter}</div>}
                            </div>
                        ) : (
                            progressLabel && <div className={s.phaseOnlyLabel}>{progressLabel}</div>
                        )}
                    </div>
                </div>
            </div>

            {details.length > 0 && (
                <div className={`${s.shelfClip} ${shelfOpen ? s.shelfClipOpen : ''}`} aria-hidden={!shelfOpen}>
                    <div className={s.shelf}>
                        <div className={s.shelfTitle}>Batch details</div>
                        <div className={s.shelfGrid}>
                            {details.map((detail) => (
                                <div key={detail.label} className={s.shelfRow}>
                                    <span className={s.shelfLabel}>{detail.label}</span>
                                    <span className={s.shelfValue}>{detail.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default BatchWidgetCard
